import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Loader2, LogOut, Radar, RefreshCw,
  FileCheck2, TrendingUp, TrendingDown, Minus, ExternalLink, Calendar,
} from "lucide-react";
import LoginForm from "@/components/LoginForm";
import { SectionLabel } from "@/components/Reveal";
import { Radar4D, ScoreBars } from "@/components/Radar4D";
import { DIMENSIONS } from "@/diagnosticData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_INFO = [
  { id: "D1", title: "Diagnóstico", tag: "Leitura", desc: "Onde estamos de verdade? Mapeamos negócio, marca, cliente, mercado e comunicação e entregamos um espelho imparcial da tração do negócio." },
  { id: "D2", title: "Direção", tag: "Escolha", desc: "Por que nos escolher, e para quem? Posicionamento, proposta de valor e fronteiras de atuação definidos e validados." },
  { id: "D3", title: "Desenho", tag: "Expressão", desc: "Como isso é percebido e lembrado? Identidade visual e verbal com lastro estratégico, pronta para execução." },
  { id: "D4", title: "Desdobramento", tag: "Ação", desc: "Como colocamos isso para funcionar? Canais, jornada comercial, indicadores e roadmap de 90 dias, 180 dias e 12 meses." },
];

const STAGE_STATUS = {
  pendente: ["Pendente", "border-white/15 text-slate-500"],
  ativa: ["Em andamento", "border-brand-orange/60 text-brand-orange"],
  concluida: ["Concluída", "border-emerald-400/50 text-emerald-400"],
};

const DELIV_STATUS = {
  previsto: ["Previsto", "border-white/15 text-slate-500"],
  producao: ["Em produção", "border-brand-orange/60 text-brand-orange"],
  entregue: ["Entregue", "border-emerald-400/50 text-emerald-400"],
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
};

const radarFromScores = (scores) =>
  DIMENSIONS.map((d) => ({ dim: d.short, valor: (scores || {})[d.short] ?? 0 }));

const AuditQuiz = ({ onSaved, onCancel }) => {
  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState(DIMENSIONS.map((d) => d.questions.map(() => null)));
  const [saving, setSaving] = useState(false);
  const dimIdx = step - 1;
  const currentDone = answers[dimIdx].every((v) => v !== null);

  const answer = (di, qi, v) =>
    setAnswers((prev) => prev.map((arr, i) => (i === di ? arr.map((x, j) => (j === qi ? v : x)) : arr)));

  const finish = async () => {
    setSaving(true);
    const dimScores = answers.map(
      (arr) => Math.round(((arr.reduce((a, b) => a + b, 0) / arr.length - 1) / 4) * 100)
    );
    const overall = Math.round(dimScores.reduce((a, b) => a + b, 0) / dimScores.length);
    try {
      await axios.post(
        `${API}/portal/audits`,
        { scores: Object.fromEntries(DIMENSIONS.map((d, i) => [d.short, dimScores[i]])), overall },
        { withCredentials: true }
      );
      toast.success("Auditoria registrada. Seu radar foi atualizado.");
      onSaved();
    } catch {
      toast.error("Não foi possível salvar a auditoria.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      data-testid="portal-audit-quiz"
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="border border-white/10 bg-navy/40 p-6 backdrop-blur-sm lg:p-10"
    >
      <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
        <span>
          Etapa {step} de 5 — {DIMENSIONS[dimIdx].title}
        </span>
        <span>{answers.flat().filter((v) => v !== null).length}/20</span>
      </div>
      <div className="mt-3 h-[3px] w-full bg-white/10">
        <motion.div
          className="h-full bg-gradient-to-r from-brand-orange to-brand-magenta"
          animate={{ width: `${(answers.flat().filter((v) => v !== null).length / 20) * 100}%` }}
          transition={{ duration: 0.5 }}
        />
      </div>
      <div className="mt-8">
        {DIMENSIONS[dimIdx].questions.map((q, qi) => (
          <div key={qi} className="border-b border-white/10 py-6 last:border-0">
            <p className="text-sm leading-relaxed text-slate-200 sm:text-base">{q}</p>
            <div className="mt-4 flex gap-2">
              {[1, 2, 3, 4, 5].map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => answer(dimIdx, qi, v)}
                  data-testid={`portal-audit-option-${dimIdx}-${qi}-${v}`}
                  className={`h-10 flex-1 border font-mono text-sm transition-all duration-300 ${
                    answers[dimIdx][qi] === v
                      ? "border-brand-orange bg-brand-orange text-white"
                      : "border-white/15 text-slate-400 hover:border-brand-orange/60 hover:text-white"
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-8 flex items-center justify-between">
        <button
          onClick={() => (step > 1 ? setStep(step - 1) : onCancel())}
          data-testid="portal-audit-back"
          className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-400 transition-colors hover:text-white"
        >
          <ArrowLeft size={14} /> {step > 1 ? "Voltar" : "Cancelar"}
        </button>
        <button
          onClick={() => (step < 5 ? setStep(step + 1) : finish())}
          disabled={!currentDone || saving}
          data-testid="portal-audit-next"
          className="flex items-center gap-3 bg-brand-orange px-7 py-3.5 font-mono text-xs uppercase tracking-[0.22em] text-white transition-colors hover:bg-brand-red disabled:opacity-40"
        >
          {saving ? "Salvando..." : step < 5 ? "Avançar" : "Salvar auditoria"}
          <ArrowRight size={15} />
        </button>
      </div>
    </motion.div>
  );
};

const Dashboard = ({ user, onLogout }) => {
  const [data, setData] = useState(null);
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quizOpen, setQuizOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [proj, aud] = await Promise.all([
        axios.get(`${API}/portal/project`, { withCredentials: true }),
        axios.get(`${API}/portal/audits`, { withCredentials: true }),
      ]);
      setData(proj.data);
      setAudits(aud.data.audits);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else toast.error("Não foi possível carregar seu painel.");
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } finally {
      onLogout();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-3 py-24 text-slate-500">
        <Loader2 size={18} className="animate-spin" /> Carregando seu painel...
      </div>
    );
  }

  const project = data?.project;
  const latest = audits[0];
  const previous = audits[1];
  const delta = latest && previous ? latest.overall - previous.overall : null;
  const currentStage = project
    ? STAGE_INFO.find((s) => (project.stages?.[s.id]?.status || "pendente") === "ativa")?.id ||
      (project.stages?.D4?.status === "concluida" ? "D4" : "D1")
    : null;

  return (
    <div data-testid="portal-dashboard" className="mx-auto w-full max-w-6xl">
      <div className="flex flex-wrap items-center justify-between gap-6">
        <div className="flex items-center gap-5">
          {project && project.logo && (
            <img
              src={project.logo}
              alt={project.company || "Logomarca do cliente"}
              data-testid="portal-client-logo"
              className="h-14 w-auto max-w-[140px] border border-white/10 bg-white/5 object-contain p-2"
            />
          )}
          <div>
            <SectionLabel>Área do cliente</SectionLabel>
          <h1 className="mt-4 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
            Olá, {user.name?.split(" ")[0] || "bem-vindo"}.
          </h1>
          {project && (
            <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
              {project.company || user.email} · {project.plan} · Etapa atual: {currentStage || "—"}
            </p>
          )}
          </div>
        </div>
        <div className="flex gap-3">
          <Link
            to={`/agendar?ref=${user.id}`}
            data-testid="portal-booking-link"
            className="flex items-center gap-2 border border-brand-orange/60 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-brand-orange transition-colors hover:bg-brand-orange hover:text-white"
          >
            <Calendar size={14} /> Agendar sessão
          </Link>
          <button
            onClick={load}
            data-testid="portal-refresh-button"
            className="flex items-center gap-2 border border-white/20 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-white transition-colors hover:border-brand-orange hover:text-brand-orange"
          >
            <RefreshCw size={14} /> Atualizar
          </button>
          <button
            onClick={logout}
            data-testid="portal-logout-button"
            className="flex items-center gap-2 border border-white/20 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-white transition-colors hover:border-brand-red hover:text-brand-red"
          >
            <LogOut size={14} /> Sair
          </button>
        </div>
      </div>

      {!project ? (
        <div data-testid="portal-no-project" className="mt-12 border border-dashed border-white/15 p-12 text-center">
          <p className="text-sm text-slate-400">
            Seu projeto está sendo configurado pela equipe Fuchs. Assim que estiver pronto, os dados da
            compra, o cronograma e os entregáveis aparecem aqui.
          </p>
        </div>
      ) : (
        <>
          <div data-testid="portal-purchase-card" className="mt-12 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
            {[
              ["Formato contratado", project.plan],
              ["Investimento", project.value || "Sob consulta"],
              ["Início do projeto", project.start_date || "—"],
              ["Status", project.status === "ativo" ? "Ativo" : project.status],
              ["Telefone", project.phone || "—"],
              ["CNPJ", project.cnpj || "—"],
              ["Contato financeiro", project.financial_contact || "—"],
            ].map(([label, value]) => (
              <div key={label} className="bg-ink p-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">{label}</p>
                <p className="mt-3 font-display text-lg font-bold text-white">{value}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 grid gap-10 lg:grid-cols-2">
            <div className="border border-white/10 bg-navy/40 p-6 lg:p-8">
              <div className="flex items-center justify-between">
                <h2 className="font-display text-xl font-bold text-white">Radar estratégico</h2>
                {latest && (
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    {fmtDate(latest.created_at)}
                  </span>
                )}
              </div>
              {latest ? (
                <div data-testid="portal-radar" className="mt-6">
                  <Radar4D data={radarFromScores(latest.scores)} caption="Sua maturidade mais recente" />
                </div>
              ) : (
                <p className="mt-6 text-sm leading-relaxed text-slate-400">
                  Nenhuma auditoria registrada ainda. Faça a primeira para gerar o radar estratégico da sua marca.
                </p>
              )}
            </div>

            <div className="border border-white/10 bg-navy/40 p-6 lg:p-8">
              <div className="flex items-center justify-between">
                <h2 className="font-display text-xl font-bold text-white">Auditoria de maturidade</h2>
                {!quizOpen && (
                  <button
                    onClick={() => setQuizOpen(true)}
                    data-testid="portal-new-audit-button"
                    className="border border-brand-orange/60 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-brand-orange transition-colors hover:bg-brand-orange hover:text-white"
                  >
                    Nova auditoria
                  </button>
                )}
              </div>
              {quizOpen ? (
                <div className="mt-6">
                  <AuditQuiz
                    onSaved={() => {
                      setQuizOpen(false);
                      load();
                    }}
                    onCancel={() => setQuizOpen(false)}
                  />
                </div>
              ) : (
                <div data-testid="portal-audit-list" className="mt-6 space-y-4">
                  {latest && (
                    <div className="flex items-end gap-4 border-b border-white/10 pb-5">
                      <p className="font-display text-6xl font-extrabold leading-none text-white">{latest.overall}</p>
                      <div className="pb-1">
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">índice atual /100</p>
                        {delta !== null && (
                          <p className={`mt-1 flex items-center gap-1 font-mono text-[11px] ${delta > 0 ? "text-emerald-400" : delta < 0 ? "text-brand-red" : "text-slate-500"}`}>
                            {delta > 0 ? <TrendingUp size={13} /> : delta < 0 ? <TrendingDown size={13} /> : <Minus size={13} />}
                            {delta > 0 ? `+${delta}` : delta} vs. auditoria anterior
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                  {audits.length === 0 && (
                    <p className="text-sm text-slate-500">Reavalie a maturidade a cada ciclo do projeto para medir a evolução.</p>
                  )}
                  {audits.map((a) => (
                    <div key={a.id} className="flex items-center justify-between border-b border-white/5 pb-3 text-sm">
                      <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">{fmtDate(a.created_at)}</span>
                      <span className="flex items-baseline gap-3">
                        <span className="font-mono text-[10px] text-slate-600">
                          {DIMENSIONS.map((d) => `${d.short.slice(0, 3)} ${a.scores?.[d.short] ?? "—"}`).join(" · ")}
                        </span>
                        <span className="font-display text-lg font-bold text-white">{a.overall}</span>
                      </span>
                    </div>
                  ))}
                  {latest && (
                    <div className="pt-2">
                      <ScoreBars data={radarFromScores(latest.scores)} caption="Distribuição por dimensão" />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="mt-16">
            <SectionLabel>Cronograma do método</SectionLabel>
            <h2 className="mt-5 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
              As quatro etapas do seu projeto.
            </h2>
            <div className="mt-10 space-y-6">
              {STAGE_INFO.map((info, i) => {
                const st = project.stages?.[info.id] || { status: "pendente", start: "", end: "", deliverables: [] };
                const [sLabel, sCls] = STAGE_STATUS[st.status] || STAGE_STATUS.pendente;
                return (
                  <motion.div
                    key={info.id}
                    data-testid={`portal-stage-${info.id.toLowerCase()}`}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: i * 0.05 }}
                    className={`border p-7 lg:p-9 ${
                      st.status === "ativa" ? "border-brand-orange/50 bg-navy/50" : "border-white/10 bg-navy/20"
                    }`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div className="flex items-baseline gap-4">
                        <span className="font-display text-3xl font-extrabold text-outline-orange">{info.id}</span>
                        <div>
                          <h3 className="font-display text-xl font-bold text-white sm:text-2xl">{info.title}</h3>
                          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand-magenta">{info.tag}</p>
                        </div>
                      </div>
                      <span className={`border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] ${sCls}`}>
                        {sLabel}
                      </span>
                    </div>
                    <p className="mt-4 max-w-3xl text-sm leading-relaxed text-slate-400">{info.desc}</p>
                    {(st.start || st.end) && (
                      <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">
                        {st.start ? `Início: ${st.start}` : ""}
                        {st.start && st.end ? " · " : ""}
                        {st.end ? `Previsão: ${st.end}` : ""}
                      </p>
                    )}
                    {st.message && (
                      <p data-testid={`portal-stage-message-${info.id.toLowerCase()}`} className="mt-4 max-w-3xl border-l-2 border-brand-magenta pl-4 font-serif text-sm italic leading-relaxed text-slate-300">
                        {st.message}
                      </p>
                    )}
                    {st.deliverables?.length > 0 && (
                      <div className="mt-6 border-t border-white/10 pt-6">
                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Entregáveis</p>
                        <ul className="mt-4 space-y-3">
                          {st.deliverables.map((d) => {
                            const [dLabel, dCls] = DELIV_STATUS[d.status] || DELIV_STATUS.previsto;
                            return (
                              <li key={d.id} data-testid={`portal-deliverable-${d.id}`} className="flex flex-wrap items-center justify-between gap-3 text-sm">
                                <span className="flex items-center gap-3 text-slate-200">
                                  <FileCheck2 size={15} className="shrink-0 text-brand-orange" />
                                  {d.name}
                                </span>
                                <span className="flex items-center gap-3">
                                  <span className={`border px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.18em] ${dCls}`}>
                                    {dLabel}
                                  </span>
                                  {d.link && d.status === "entregue" && (
                                    <a
                                      href={d.link}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      data-testid={`portal-deliverable-link-${d.id}`}
                                      className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.18em] text-brand-orange hover:underline"
                                    >
                                      Abrir <ExternalLink size={11} />
                                    </a>
                                  )}
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default function Portal() {
  const [auth, setAuth] = useState(undefined);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then(({ data }) => setAuth(data))
      .catch(() => setAuth(null));
  }, []);

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="min-h-screen bg-ink px-6 pb-24 pt-36 lg:px-10 lg:pt-44"
    >
      {auth === undefined ? (
        <div className="flex items-center justify-center gap-3 py-24 text-slate-500">
          <Loader2 size={18} className="animate-spin" /> Verificando acesso...
        </div>
      ) : auth === null ? (
        <LoginForm title="Área do Cliente" subtitle="Painel operacional do seu projeto" prefix="portal" onSuccess={setAuth} />
      ) : auth.role !== "client" ? (
        <div className="mx-auto max-w-md border border-white/10 bg-navy/40 p-10 text-center">
          <p className="text-sm text-slate-300">Esta conta é da equipe Fuchs.</p>
          <Link
            to="/admin"
            data-testid="portal-goto-admin"
            className="mt-6 inline-block border border-brand-orange/60 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-brand-orange transition-colors hover:bg-brand-orange hover:text-white"
          >
            Ir para a Área de Gerenciamento
          </Link>
        </div>
      ) : (
        <Dashboard user={auth} onLogout={() => setAuth(null)} />
      )}
    </motion.main>
  );
}
